/**
 * SAFE MODE: City Normalization Utility
 * 
 * Maps Arabic city names (from Google Geocoding) to pricing city_key.
 * This ensures city-based pricing multipliers work correctly.
 * 
 * DO NOT modify pricing logic - this only normalizes city names.
 */

// City key to Arabic names mapping (including common variations)
const CITY_ALIASES = {
  riyadh: [
    'الرياض',
    'رياض',
    'Riyadh',
    'الرياض العاصمة'
  ],
  jeddah: [
    'جدة',
    'جده',
    'Jeddah',
    'Jiddah',
    'محافظة جدة'
  ],
  dammam: [
    'الدمام',
    'دمام',
    'Dammam',
    'محافظة الدمام',
    'الظهران',       // Dhahran - part of Dammam metro
    'ظهران',
    'Dhahran'
  ],
  khobar: [
    'الخبر',
    'خبر',
    'Khobar',
    'Al Khobar',
    'الخُبَر'
  ],
  mecca: [
    'مكة المكرمة',
    'مكة',
    'مكه',
    'Mecca',
    'Makkah',
    'محافظة مكة المكرمة'
  ],
  medina: [
    'المدينة المنورة',
    'المدينة',
    'المدينه',
    'Medina',
    'Madinah',
    'محافظة المدينة المنورة'
  ],
  taif: [
    'الطائف',
    'طائف',
    'Taif',
    'محافظة الطائف'
  ],
  tabuk: [
    'تبوك',
    'Tabuk',
    'منطقة تبوك'
  ],
  abha: [
    'أبها',
    'ابها',
    'Abha',
    'محافظة أبها'
  ],
  buraidah: [
    'بريدة',
    'بريده',
    'Buraidah',
    'Buraydah',
    'القصيم',
    'منطقة القصيم'
  ],
  najran: [
    'نجران',
    'Najran',
    'منطقة نجران',
    'محافظة نجران'
  ],
  hail: [
    'حائل',
    'حايل',
    'Hail',
    'Ha\'il',
    'منطقة حائل'
  ],
  jazan: [
    'جازان',
    'جيزان',
    'Jazan',
    'Jizan',
    'منطقة جازان'
  ],
  alahsa: [
    'الأحساء',
    'الاحساء',
    'Al-Ahsa',
    'Al Ahsa',
    'الهفوف',
    'محافظة الأحساء'
  ],
  yanbu: [
    'ينبع',
    'Yanbu',
    'ينبع البحر'
  ],
  jubail: [
    'الجبيل',
    'جبيل',
    'Jubail',
    'Al Jubail'
  ]
};

// Region to default city mapping (fallback if city not found)
const REGION_DEFAULT_CITIES = {
  eastern: 'dammam',
  central: 'riyadh',
  western: 'jeddah',
  northern: 'tabuk',
  southern: 'abha'
};

// Build reverse lookup map for faster matching
const buildReverseLookup = () => {
  const lookup = {};
  Object.entries(CITY_ALIASES).forEach(([cityKey, aliases]) => {
    aliases.forEach(alias => {
      // Normalize alias for lookup
      const normalized = alias.toLowerCase().trim();
      lookup[normalized] = cityKey;
    });
  });
  return lookup;
};

const CITY_LOOKUP = buildReverseLookup();

/**
 * Normalize a city name to its pricing key
 * 
 * @param {string} cityName - The city name from Google Geocoding (Arabic or English)
 * @param {string} region - Optional region for fallback
 * @returns {{ cityKey: string, cityLabel: string, matched: boolean }}
 */
export const normalizeCity = (cityName, region = null) => {
  if (!cityName) {
    // Fallback to region default if no city name
    if (region && REGION_DEFAULT_CITIES[region]) {
      const defaultCityKey = REGION_DEFAULT_CITIES[region];
      return {
        cityKey: defaultCityKey,
        cityLabel: CITY_ALIASES[defaultCityKey]?.[0] || cityName,
        matched: false,
        fallback: 'region'
      };
    }
    return {
      cityKey: '',
      cityLabel: '',
      matched: false,
      fallback: 'none'
    };
  }

  // Normalize input for lookup
  const normalized = cityName.toLowerCase().trim();
  
  // Direct lookup
  if (CITY_LOOKUP[normalized]) {
    const cityKey = CITY_LOOKUP[normalized];
    return {
      cityKey,
      cityLabel: CITY_ALIASES[cityKey]?.[0] || cityName,
      matched: true,
      fallback: 'none'
    };
  }

  // Partial match - check if any alias is contained in the city name
  for (const [cityKey, aliases] of Object.entries(CITY_ALIASES)) {
    for (const alias of aliases) {
      const normalizedAlias = alias.toLowerCase().trim();
      if (normalized.includes(normalizedAlias) || normalizedAlias.includes(normalized)) {
        return {
          cityKey,
          cityLabel: CITY_ALIASES[cityKey]?.[0] || cityName,
          matched: true,
          fallback: 'partial'
        };
      }
    }
  }

  // No match found - fallback to region default
  if (region && REGION_DEFAULT_CITIES[region]) {
    const defaultCityKey = REGION_DEFAULT_CITIES[region];
    return {
      cityKey: defaultCityKey,
      cityLabel: cityName, // Keep original name for display
      matched: false,
      fallback: 'region'
    };
  }

  // No match and no region - return empty (multiplier will be 1.0)
  return {
    cityKey: '',
    cityLabel: cityName,
    matched: false,
    fallback: 'none'
  };
};

/**
 * Get city label from key
 * @param {string} cityKey - The city key (e.g., 'riyadh')
 * @returns {string} - The Arabic label
 */
export const getCityLabel = (cityKey) => {
  return CITY_ALIASES[cityKey]?.[0] || cityKey;
};

/**
 * Get region default city key
 * @param {string} region - Region slug
 * @returns {string} - Default city key for the region
 */
export const getRegionDefaultCity = (region) => {
  return REGION_DEFAULT_CITIES[region] || '';
};

export default {
  normalizeCity,
  getCityLabel,
  getRegionDefaultCity,
  CITY_ALIASES,
  REGION_DEFAULT_CITIES
};
